import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { EMPTY_VALUE, GAP_REASONS, TONE } from '../../data/constants'
import { getCapabilityGap, getControlResult } from '../../data/scoring'
import Chip from '../../components/Chip/Chip'
import EmptyState from '../../components/EmptyState/EmptyState'
import ReportTable from '../../components/ReportTable/ReportTable'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'

const DLP_MODE_LABEL_IDS = {
  enforcement: 'AI_RISK_REPORT.DATA.LABELS.DLP_MODE.ENFORCEMENT',
  disabled: 'AI_RISK_REPORT.DATA.LABELS.DLP_MODE.DISABLED',
  test_with_notifications: 'AI_RISK_REPORT.DATA.LABELS.DLP_MODE.TEST_WITH_NOTIFICATIONS',
  test_without_notifications: 'AI_RISK_REPORT.DATA.LABELS.DLP_MODE.TEST_WITHOUT_NOTIFICATIONS',
  unknown: 'AI_RISK_REPORT.DATA.LABELS.DLP_MODE.UNKNOWN',
}

const TONE_BY_DLP_STATE = {
  ok: TONE.OK,
  warning: TONE.WARN,
  neutral: TONE.NEUTRAL,
}

const getRows = (rows = []) => rows.map((row) => ({ ...row, key: row.entityId }))

const getBooleanStatValue = (value) => {
  if (value === true) return <FormattedMessage id="AI_RISK_REPORT.LABEL.YES" />
  if (value === false) return <FormattedMessage id="AI_RISK_REPORT.LABEL.NO" />
  return EMPTY_VALUE
}

const getBooleanTone = (value) => {
  if (value === true) return TONE.OK
  if (value === false) return TONE.WARN
  return TONE.NEUTRAL
}

const getDlpModeLabelId = (mode) => DLP_MODE_LABEL_IDS[mode] ?? DLP_MODE_LABEL_IDS.unknown

const getDlpPolicyTone = (row) => TONE_BY_DLP_STATE[row.state] ?? TONE.NEUTRAL

const getDlpEnforcementCount = (dlpPolicies = []) =>
  dlpPolicies.filter((policy) => policy.mode === 'enforcement').length

const getControlTone = (result) => {
  if (result === true) return TONE.OK
  if (result === false) return TONE.WARN
  return TONE.NEUTRAL
}

const getNotAssessedMessageId = (gap, licenseRequiredMessageId, unavailableMessageId) =>
  gap?.reason === GAP_REASONS.LICENSE_REQUIRED ? licenseRequiredMessageId : unavailableMessageId

const renderLocations = (locations = []) => (locations.length > 0 ? locations.join(', ') : EMPTY_VALUE)

export default function SensitivityDlp({
  summary,
  sensitivityLabels,
  dlpPolicies,
  score,
  controls,
  gap = null,
  partialGaps = [],
}) {
  const intl = useIntl()
  const labelGap = gap ?? getCapabilityGap(partialGaps, 'sensitivityLabels')
  const dlpGap = gap ?? getCapabilityGap(partialGaps, 'dlp')
  const labelRows = labelGap ? [] : getRows(sensitivityLabels)
  const dlpPolicyRows = dlpGap ? [] : getRows(dlpPolicies)
  const labelSummary = summary?.dataGovernance?.sensitivityLabels ?? {}
  const deployedLabelCount = labelGap ? null : (labelSummary.deployed ?? 0)
  const tenantDefaultLabelSet = labelGap ? null : labelSummary.tenantDefaultLabelSet
  const dlpPoliciesInEnforcement = dlpGap ? null : getDlpEnforcementCount(dlpPolicies)

  const labelColumns = [
    {
      key: 'label',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.LABEL" />,
      render: (row) => <span className="text-body fw-bold">{row.displayName || row.labelId || EMPTY_VALUE}</span>,
      sortable: true,
      sortValue: (row) => row.displayName || row.labelId || '',
      searchValue: (row) => [row.displayName, row.labelId, row.entityId],
    },
    {
      key: 'scope',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SCOPE" />,
      render: (row) => row.scope || EMPTY_VALUE,
      sortable: true,
      sortValue: (row) => row.scope || '',
      searchValue: (row) => row.scope || EMPTY_VALUE,
    },
    {
      key: 'publishedTo',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.PUBLISHED" />,
      render: (row) => row.publishedTo || EMPTY_VALUE,
      sortable: true,
      sortValue: (row) => row.publishedTo || '',
      searchValue: (row) => row.publishedTo || EMPTY_VALUE,
    },
    {
      key: 'autoLabelingConfigured',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.AUTO_LABELING_RULE" />,
      render: (row) => (
        <Chip tone={row.autoLabelingConfigured ? TONE.OK : TONE.NEUTRAL}>
          <FormattedMessage
            id={
              row.autoLabelingConfigured
                ? 'AI_RISK_REPORT.DATA.LABELS.AUTO_CONFIGURED'
                : 'AI_RISK_REPORT.DATA.LABELS.AUTO_NOT_CONFIGURED'
            }
          />
        </Chip>
      ),
      sortable: true,
      sortValue: (row) => (row.autoLabelingConfigured ? 1 : 0),
      searchValue: (row) =>
        intl.formatMessage({
          id: row.autoLabelingConfigured
            ? 'AI_RISK_REPORT.DATA.LABELS.AUTO_CONFIGURED'
            : 'AI_RISK_REPORT.DATA.LABELS.AUTO_NOT_CONFIGURED',
        }),
    },
  ]
  const dlpColumns = [
    {
      key: 'policy',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.POLICY" />,
      render: (row) => <span className="text-body fw-bold">{row.displayName || row.policyId || EMPTY_VALUE}</span>,
      sortable: true,
      sortValue: (row) => row.displayName || row.policyId || '',
      searchValue: (row) => [row.displayName, row.policyId, row.entityId],
    },
    {
      key: 'state',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.STATE" />,
      render: (row) => (
        <Chip tone={getDlpPolicyTone(row)}>
          <FormattedMessage id={getDlpModeLabelId(row.mode)} />
        </Chip>
      ),
      sortable: true,
      sortValue: (row) => row.mode,
      searchValue: (row) => [intl.formatMessage({ id: getDlpModeLabelId(row.mode) }), row.mode, row.state],
    },
    {
      key: 'locations',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.LOCATIONS" />,
      render: (row) => renderLocations(row.locations),
      sortable: true,
      sortValue: (row) => renderLocations(row.locations),
      searchValue: (row) => (row.locations?.length ? row.locations : EMPTY_VALUE),
    },
  ]
  const stats = [
    {
      key: 'labels',
      value: deployedLabelCount ?? EMPTY_VALUE,
      denom: labelGap ? undefined : (labelSummary.total ?? 0),
      tone: getControlTone(getControlResult(controls, 'labels.deployed')),
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.LABELS.STAT_LABELS" />,
    },
    {
      key: 'dlpEnforce',
      value: dlpPoliciesInEnforcement ?? EMPTY_VALUE,
      denom: dlpGap ? undefined : dlpPolicies.length,
      tone: getControlTone(getControlResult(controls, 'labels.dlpEnforced')),
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.LABELS.STAT_DLP_ENFORCE" />,
    },
    {
      key: 'defaultLabel',
      value: getBooleanStatValue(tenantDefaultLabelSet),
      tone: getBooleanTone(tenantDefaultLabelSet),
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.LABELS.STAT_DEFAULT" />,
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.LABELS.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.LABELS.SUBTITLE"
      score={score.value}
      tone={score.tone}
      gap={gap}
      partialGap={partialGaps[0]}
      stats={stats}
    >
      <SubArea titleId="AI_RISK_REPORT.DATA.LABELS.INVENTORY_TITLE">
        <ReportTable
          columns={labelColumns}
          rows={labelRows}
          affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.SENSITIVITY_LABELS"
          isAffectedEntityTable={false}
          empty={
            <EmptyState
              messageId={
                labelGap
                  ? getNotAssessedMessageId(
                      labelGap,
                      'AI_RISK_REPORT.DATA.LABELS.LABELS_LICENSE_REQUIRED',
                      'AI_RISK_REPORT.DATA.LABELS.LABELS_NOT_ASSESSED',
                    )
                  : 'AI_RISK_REPORT.DATA.LABELS.NO_LABELS'
              }
            />
          }
        />
      </SubArea>
      <SubArea titleId="AI_RISK_REPORT.DATA.LABELS.DLP_TITLE">
        <ReportTable
          columns={dlpColumns}
          rows={dlpPolicyRows}
          affectedEntityLabelId="AI_RISK_REPORT.TABLE.ENTITY.DLP_POLICIES"
          isAffectedEntityTable={false}
          empty={
            <EmptyState
              messageId={
                dlpGap
                  ? getNotAssessedMessageId(
                      dlpGap,
                      'AI_RISK_REPORT.DATA.LABELS.DLP_LICENSE_REQUIRED',
                      'AI_RISK_REPORT.DATA.LABELS.DLP_NOT_ASSESSED',
                    )
                  : 'AI_RISK_REPORT.DATA.LABELS.NO_DLP_POLICIES'
              }
            />
          }
        />
      </SubArea>
    </SubBlock>
  )
}

SensitivityDlp.propTypes = {
  summary: PropTypes.shape({
    dataGovernance: PropTypes.shape({
      sensitivityLabels: PropTypes.shape({
        total: PropTypes.number,
        deployed: PropTypes.number,
        tenantDefaultLabelSet: PropTypes.bool,
      }),
    }),
  }).isRequired,
  sensitivityLabels: PropTypes.arrayOf(
    PropTypes.shape({
      entityId: PropTypes.string.isRequired,
      labelId: PropTypes.string,
      displayName: PropTypes.string,
      scope: PropTypes.string,
      publishedTo: PropTypes.string,
      autoLabelingConfigured: PropTypes.bool,
      isPublished: PropTypes.bool,
      isDisabled: PropTypes.bool,
      priority: PropTypes.number,
    }),
  ).isRequired,
  dlpPolicies: PropTypes.arrayOf(
    PropTypes.shape({
      entityId: PropTypes.string.isRequired,
      policyId: PropTypes.string,
      displayName: PropTypes.string,
      mode: PropTypes.string,
      state: PropTypes.string,
      locations: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  controls: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, result: PropTypes.bool })).isRequired,
  gap: PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string }),
  partialGaps: PropTypes.arrayOf(
    PropTypes.shape({ reason: PropTypes.string, requiredLicense: PropTypes.string, capabilityId: PropTypes.string }),
  ),
}
